<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import Swal from "sweetalert2";
import defaultAvatar from "@/assets/images/users/user-dummy-img.jpg";
import { useUserProfileModule } from "@/state/pinia";

const buildDisplayName = (firstName, lastName, fallback = "") => {
  const derivedName = `${firstName || ""} ${lastName || ""}`.trim();
  return derivedName || fallback;
};

export default {
  name: "UserProfileView",
  components: { Layout, PageHeader },
  data() {
    return {
      localProfile: {
        firstName: "",
        lastName: "",
        phone: "",
        title: "",
        department: "",
        note: "",
      },
      avatarFile: null,
      avatarPreviewUrl: "",
      avatarObjectUrlForCleanup: "",
      savingProfile: false,
      savingAvatar: false,
      savingPassword: false,
      passwordForm: {
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      },
      defaultAvatar,
    };
  },
  computed: {
    profileStore() {
      return useUserProfileModule();
    },
    profile() {
      return this.profileStore.profile || {};
    },
    loading() {
      return this.profileStore.loading;
    },
    displayName() {
      return (
        buildDisplayName(
          this.profile.firstName,
          this.profile.lastName,
          this.profile.displayName
        ) || "Użytkownik"
      );
    },
    avatarPreview() {
      return this.avatarPreviewUrl || this.profile.avatarUrl || this.defaultAvatar;
    },
    roles() {
      return this.profile.roles || [];
    },
  },
  watch: {
    profile: {
      handler() {
        this.syncLocalProfile();
      },
      deep: true,
    },
  },
  created() {
    this.loadProfile();
  },
  beforeUnmount() {
    if (this.avatarObjectUrlForCleanup) {
      URL.revokeObjectURL(this.avatarObjectUrlForCleanup);
    }
  },
  methods: {
    loadProfile() {
      this.profileStore
        .httpFetchMyProfile()
        .then(() => {
          this.syncLocalProfile();
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się załadować profilu.", "error");
        });
    },
    syncLocalProfile() {
      const {
        firstName = "",
        lastName = "",
        phone = "",
        title = "",
        department = "",
        note = "",
      } = this.profile;
      this.localProfile = {
        firstName,
        lastName,
        phone,
        title,
        department,
        note,
      };
      if (!this.avatarFile) {
        this.avatarPreviewUrl = "";
      }
    },
    handleAvatarChange(event) {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      if (this.avatarObjectUrlForCleanup) {
        URL.revokeObjectURL(this.avatarObjectUrlForCleanup);
      }
      this.avatarFile = file;
      this.avatarPreviewUrl = URL.createObjectURL(file);
      this.avatarObjectUrlForCleanup = this.avatarPreviewUrl;
    },
    saveAvatar() {
      if (!this.avatarFile || this.savingAvatar) {
        return;
      }
      this.savingAvatar = true;
      this.profileStore
        .httpUploadMyAvatar(this.avatarFile)
        .then(() => {
          Swal.fire("Zapisano", "Twój awatar został zaktualizowany.", "success");
          this.avatarFile = null;
          this.avatarPreviewUrl = "";
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się zapisać awatara.", "error");
        })
        .finally(() => {
          this.savingAvatar = false;
        });
    },
    saveProfile() {
      if (this.savingProfile) {
        return;
      }
      this.savingProfile = true;
      const payload = {
        ...this.localProfile,
        displayName: buildDisplayName(
          this.localProfile.firstName,
          this.localProfile.lastName,
          this.profile.displayName
        ),
      };
      this.profileStore
        .httpSaveMyProfile(payload)
        .then(() => {
          Swal.fire("Zapisano", "Twój profil został zaktualizowany.", "success");
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się zapisać profilu.", "error");
        })
        .finally(() => {
          this.savingProfile = false;
        });
    },
    submitPasswordChange() {
      if (this.savingPassword) {
        return;
      }
      const { oldPassword, newPassword, confirmNewPassword } = this.passwordForm;
      if (!oldPassword || !newPassword || !confirmNewPassword) {
        Swal.fire("Brakujące pola", "Proszę wypełnić wszystkie pola hasła.", "warning");
        return;
      }
      if (newPassword.length < 8) {
        Swal.fire("Słabe hasło", "Nowe hasło musi mieć co najmniej 8 znaków.", "warning");
        return;
      }
      if (newPassword !== confirmNewPassword) {
        Swal.fire("Niezgodność", "Potwierdzenie nowego hasła nie zgadza się.", "warning");
        return;
      }
      this.savingPassword = true;
      this.profileStore
        .httpChangeMyPassword(oldPassword, newPassword)
        .then(() => {
          Swal.fire("Zaktualizowano", "Twoje hasło zostało zmienione.", "success");
          this.passwordForm = {
            oldPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          };
        })
        .catch((error) => {
          Swal.fire("Błąd", error?.message || "Nie udało się zaktualizować hasła.", "error");
        })
        .finally(() => {
          this.savingPassword = false;
        });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Profil" pageTitle="Użytkownik" />

    <div class="container-fluid">
      <div v-if="!profile" class="d-flex justify-content-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Ładowanie...</span>
        </div>
      </div>

      <div v-else>
        <div class="row">
          <div class="col-xl-4">
            <div class="card">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <img
                    :src="avatarPreview"
                    :alt="`Zdjęcie profilowe użytkownika ${displayName}`"
                    class="rounded-circle avatar-lg img-thumbnail me-3"
                  />
                  <div>
                    <h5 class="mb-1">{{ displayName }}</h5>
                    <p class="text-muted mb-1">{{ profile.email }}</p>
                    <div>
                      <span v-for="role in roles" :key="role" class="badge bg-soft-primary text-primary me-1">
                        {{ role }}
                      </span>
                    </div>
                  </div>
                </div>
                <hr class="my-4" />
                <div class="mb-3">
                  <label class="form-label">Zaktualizuj awatar</label>
                  <input type="file" class="form-control" accept="image/*" @change="handleAvatarChange" />
                </div>
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  :disabled="!avatarFile || savingAvatar"
                  @click="saveAvatar"
                >
                  Zapisz awatar
                </button>
              </div>
            </div>
          </div>

          <div class="col-xl-8">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">Informacje osobiste</h5>
                <form @submit.prevent="saveProfile">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Imię</label>
                      <input v-model="localProfile.firstName" type="text" class="form-control" />
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Nazwisko</label>
                      <input v-model="localProfile.lastName" type="text" class="form-control" />
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Telefon</label>
                      <input v-model="localProfile.phone" type="tel" class="form-control" />
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Tytuł</label>
                      <input v-model="localProfile.title" type="text" class="form-control" />
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Dział</label>
                      <input v-model="localProfile.department" type="text" class="form-control" />
                    </div>
                    <div class="col-12 mb-3">
                      <label class="form-label">Notatka</label>
                      <textarea v-model="localProfile.note" rows="3" class="form-control"></textarea>
                    </div>
                  </div>
                  <button class="btn btn-primary" type="submit" :disabled="savingProfile">
                    Zapisz profil
                  </button>
                </form>
              </div>
            </div>

            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">Zmień hasło</h5>
                <form @submit.prevent="submitPasswordChange">
                  <div class="mb-3">
                    <label class="form-label">Aktualne hasło</label>
                    <input v-model="passwordForm.oldPassword" type="password" class="form-control" />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Nowe hasło</label>
                    <input v-model="passwordForm.newPassword" type="password" class="form-control" />
                    <small class="text-muted">Minimum 8 znaków.</small>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Potwierdź nowe hasło</label>
                    <input v-model="passwordForm.confirmNewPassword" type="password" class="form-control" />
                  </div>
                  <button class="btn btn-outline-primary" type="submit" :disabled="savingPassword">
                    Zaktualizuj hasło
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
